import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiGet, apiGetBlob, apiPost, calcLineGST, fmtCurrency as formatCurrency, fmtDate, round2, isPeriodClosedError, fmtWhtCode } from "@/utils/api";
import { DueInvoicingWorklist } from "@/components/DueInvoicingWorklist";
import { SupplementalChargeEditor } from "@/components/SupplementalChargeEditor";
import { SapDraftProgress } from "@/components/SapDraftProgress";
import { SapLifecycleBadge } from "@/components/SapLifecycleBadge";
import { StatusBadge } from "@/components/StatusBadge";
import SupplierCreditForm from "@/components/SupplierCreditForm";
import { InvoiceImageZoom } from "@/components/InvoiceImageZoom";
import { PdfAttachment } from "@/components/ThreeWayMatchPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ChevronRight, CheckCircle, AlertCircle, AlertTriangle, Loader2, Plus, Trash2, Send, Search, ArrowLeft, CalendarClock, Ban, ImageOff, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { goBackToRegister } from "@/utils/registerNav";

const STEPS = [
  { id: 1, label: "Invoice Source" },
  { id: 2, label: "Review GRNs" },
  { id: 3, label: "Invoice Details & SAP Post" },
];

export default function NewInvoice() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep]                     = useState(() => {
    // Deterministic initial step: when the URL carries ``prepare=1`` + a match
    // or worklist, we start on step 2 without waiting for the async
    // invoice-sources fetch to resolve. Prevents a race where ``setStep(2)``
    // inside ``handleSourceLoaded`` effectively never runs (fetch rejects or
    // user re-navigates before the promise settles).
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("prepare") === "1" && (sp.get("match") || sp.get("worklist"))) return 2;
    } catch (_) {}
    return 1;
  });
  const [shipmentData, setShipmentData]     = useState(null);
  const [lineItems, setLineItems]           = useState([]);
  const [header, setHeader]                 = useState({ invoice_number: "", invoice_date: "", posting_date: "", payment_terms: "NET30" });
  const [saving, setSaving]                 = useState(false);
  const [posting, setPosting]               = useState(false);
  const [finalizing, setFinalizing]         = useState(false);
  const [voiding, setVoiding]               = useState(false);
  const [savedInvoice, setSavedInvoice]     = useState(null);
  const [postedInvoice, setPostedInvoice]   = useState(null);
  const [showSupplierCredit, setShowSupplierCredit] = useState(false);
  const [threeWayMatchId, setThreeWayMatchId] = useState("");
  const [approvedMatch, setApprovedMatch]     = useState(null);
  const [chargeCatalog, setChargeCatalog]     = useState(null);
  const [supplementalDraftNotice, setSupplementalDraftNotice] = useState("");
  const [sapChecking, setSapChecking] = useState(false);
  const [sapProgress, setSapProgress] = useState(null);
  const [invoiceImageUrl, setInvoiceImageUrl] = useState(null);
  const [invoiceImageType, setInvoiceImageType] = useState(null);
  const [invoiceImageState, setInvoiceImageState] = useState("loading"); // loading | ready | unavailable
  const [invoiceCopyModalOpen, setInvoiceCopyModalOpen] = useState(false);
  // Confirming via the step-2 popup only sets this in memory — it is NOT
  // persisted independently of the invoice itself. A reload before "Save
  // SAP Draft" would otherwise leave the lock engaged with the in-memory
  // charges gone (dead end: nothing to show, nothing addable). The lock
  // only survives a reload when the existing-invoice recovery below finds
  // a REAL saved draft that already carries landed-cost charge lines —
  // i.e. it tracks what was actually persisted, not a bare flag.
  const [chargesLocked, setChargesLocked] = useState(false);

  // "Back to register" only strips the URL's query params and navigates to
  // the SAME /invoices/new path — React Router does not remount this
  // component for a same-path navigation, so without an explicit local
  // reset the page silently stayed on step 3 with all its stale state,
  // making the button look like it does nothing. Reset the wizard state
  // directly before handing off to goBackToRegister (which still handles
  // the close-the-opened-tab behavior).
  const backToRegister = () => {
    setStep(1);
    setShipmentData(null);
    setLineItems([]);
    setHeader({ invoice_number: "", invoice_date: "", posting_date: "", payment_terms: "NET30" });
    setSavedInvoice(null);
    setPostedInvoice(null);
    setThreeWayMatchId("");
    setApprovedMatch(null);
    setChargesLocked(false);
    setSapProgress(null);
    setSupplementalDraftNotice("");
    setInvoiceImageUrl(null);
    setInvoiceImageState("loading");
    setInvoiceCopyModalOpen(false);
    goBackToRegister(navigate);
  };

  const fmtCurrency = (amount) => formatCurrency(amount, shipmentData?.currency || "INR");
  // Materials Hub webhook upgrade (Feb 2026): hard-block SAP Check / Save SAP
  // Draft when upstream has already recorded an invoice or credit-memo for any
  // line on this shipment (prevents double-posting).
  const invoiceExceptionHoldBlocked = Boolean(shipmentData?.invoice_exception_hold_active);
  const invoiceExceptionHolds = Array.isArray(shipmentData?.invoice_exception_holds) ? shipmentData.invoice_exception_holds : [];

  useEffect(() => {
    const source = searchParams.get("source"), reference = searchParams.get("reference"), matchId = searchParams.get("match"), hub = searchParams.get("hub") || "prod";
    // Keep ``step`` in sync with the URL on in-component navigation
    // (e.g. clicking "Continue invoice" on the embedded worklist updates
    // ``searchParams`` without remounting NewInvoice, so the initial-state
    // step derivation above would not re-run).
    if (searchParams.get("prepare") === "1" && (matchId || searchParams.get("worklist"))) {
      setStep((current) => (current < 2 ? 2 : current));
    }
    if (!source || !reference || !["shipment", "notification"].includes(source)) return;
    const params = { source, reference };
    if (source === "shipment") params.hub = hub;
    (async () => {
      try {
        handleSourceLoaded(await apiGet(`/invoice-sources?${new URLSearchParams(params)}`));
      } catch (error) {
        toast.error(error.message || "Could not open receipt details");
      }
      if (!matchId) return;
      let match;
      try {
        match = await apiGet(`/three-way-matches/approved/${encodeURIComponent(matchId)}`);
      } catch (error) {
        toast.error(error.message || "Approved three-way match could not be loaded");
        return;
      }
      setThreeWayMatchId(match.match_id); setApprovedMatch(match);
      setHeader((current) => ({ ...current, invoice_number: match.supplier_invoice_number, invoice_date: match.supplier_invoice_date, posting_date: current.posting_date || match.supplier_invoice_date }));
      // Recovery: reopening this URL (e.g. after a page refresh, or coming
      // back from the register) must NOT reset a draft/posted invoice that
      // already exists in SAP back to "nothing done yet" — the action
      // buttons would silently regress to Verify/Save instead of
      // Post/Raise Credit. handleSourceLoaded above always resets
      // savedInvoice/postedInvoice to null; restore them here, sequenced
      // AFTER that reset, if a real invoice already carries this match id.
      try {
        const res = await apiGet(`/invoices?search=${encodeURIComponent(reference)}&limit=20`);
        const existing = (res.invoices || []).find((inv) => inv.three_way_match_id === match.match_id && inv.status !== "superseded");
        if (existing) {
          setSavedInvoice(existing); setPostedInvoice(existing);
          // The editor otherwise shows an empty charge list / stale totals
          // for a draft that already has line items in the DB (e.g. a
          // Loading charge + a Round Off credit) — reopening must restore
          // exactly what SAP Check/Save SAP Draft will actually use so
          // edits are made against real data, not a blank slate.
          if (Array.isArray(existing.line_items) && existing.line_items.length) setLineItems(existing.line_items);
          // Re-derive the charges lock from the real saved draft, not a
          // bare client flag — resumes correctly only when charges were
          // actually persisted (see chargesLocked declaration above).
          if (existing.line_items?.some((li) => li.is_landed_cost_line)) setChargesLocked(true);
        }
      } catch { /* best-effort recovery only */ }
    })();
  }, [searchParams]);

  // Overlay the approved match's billed qty/rate on the GRN line items.
  // This is critical for the "Accept variance" exception path where the match
  // intentionally carries a billed qty/rate that differs from the GRN snapshot
  // (otherwise the invoice would silently post at GRN values and the variance
  // would never flow through to the credit-memo rebase step).
  //
  // Re-triggers on every ``lineItems`` reference change — handleSourceLoaded
  // re-creates the array when the user re-navigates to this page, and without
  // the overlay re-applying the invoiced_qty would silently reset back to the
  // GRN-received qty. The in-effect ``needsApply`` guard prevents an infinite
  // setLineItems loop.
  useEffect(() => {
    if (!approvedMatch || !Array.isArray(approvedMatch.lines) || !lineItems.length) return;
    // Two-pass, not a single OR: a shipment can carry the SAME material_code
    // on multiple PO lines (e.g. two "G12LW" lines from different POs, one
    // of which is the variance line). A single find() with an OR'd fallback
    // would stop at the FIRST element satisfying *either* condition in
    // array order, so a material_code coincidence on an earlier, unrelated
    // line could "win" over the correct PO-matched line that appears later
    // — silently applying one line's billed qty to a different line.
    // Exact po_number + po_item_number match must always take priority;
    // material_code is only a fallback when no line in the array has a
    // PO-based match for this item at all.
    const findMatchLine = (item) => {
      const byPo = approvedMatch.lines.find((m) =>
        m.po_number && m.po_item_number && String(m.po_number) === String(item.po_number) && String(m.po_item_number) === String(item.po_item_number)
      );
      if (byPo) return byPo;
      return approvedMatch.lines.find((m) => m.material_code && item.material_code && String(m.material_code) === String(item.material_code));
    };
    const needsApply = lineItems.some((item) => {
      if (item.is_landed_cost_line) return false;
      const matched = findMatchLine(item);
      if (!matched) return false;
      return (matched.quantity != null && Number(matched.quantity) !== Number(item.invoiced_qty))
        || (matched.unit_price != null && Number(matched.unit_price) !== Number(item.unit_price));
    });
    if (!needsApply) return;
    setLineItems((prev) => prev.map((item) => {
      if (item.is_landed_cost_line) return item;
      const matched = findMatchLine(item);
      if (!matched) return item;
      const nextItem = { ...item };
      if (matched.quantity != null) nextItem.invoiced_qty = Number(matched.quantity);
      if (matched.unit_price != null) nextItem.unit_price = Number(matched.unit_price);
      return nextItem;
    }));
  }, [approvedMatch, lineItems]);

  /* ── Step 1: Search ──────────────────────────────────────── */
  const handleSourceLoaded = (data) => {
      if (!searchParams.get("match") && data?.source_type === "shipment" && data?.shipment_code) {
        const previewSuffix = data?.materials_hub === "preview" ? `?${new URLSearchParams({ hub: "preview" })}` : "";
        navigate(`/invoices/prepare/direct-${encodeURIComponent(data.shipment_code)}${previewSuffix}`);
        return;
      }
      setShipmentData(data); setSavedInvoice(null); setPostedInvoice(null); setSupplementalDraftNotice("");
      setHeader((current) => ({
        ...current,
        invoice_number: current.invoice_number || data?.supplier_invoice_number || "",
        invoice_date: current.invoice_date || data?.supplier_invoice_date || "",
        // Posting Date defaults to the Supplier Invoice Date (not today) —
        // user may still override before the draft is saved. No todayISO()
        // fallback here (mirrors invoice_date exactly) — falling back
        // immediately would race with the three-way-match loader (line ~85)
        // and could "lock in" today before match.supplier_invoice_date
        // resolves, since both setters guard on `current.posting_date ||`.
        posting_date: current.posting_date || data?.supplier_invoice_date || current.invoice_date || "",
      }));
      setLineItems(
        (data?.items || []).map((item) => ({
          ...item,
          // Prefer upstream Materials Hub ``actual_invoice_qty`` when the hub
          // has already recorded an authoritative invoiced quantity; else fall
          // back to the SAP-received qty. The user may still override.
          invoiced_qty: item.actual_invoice_qty != null ? Number(item.actual_invoice_qty) : item.received_qty,
          cgst_amount: 0, sgst_amount: 0, igst_amount: 0,
        }))
      );
      apiGet(`/invoice-lookups/charge-catalog?${new URLSearchParams({ site: data?.site || "", vendor_id: data?.vendor_id || "" })}`).then((catalog) => {
        setChargeCatalog(catalog);
        // Payment terms are SAP vendor-master driven, not user-chosen (SAP Check/Save
        // SAP Draft both always use the vendor's own CashDiscountTermsCode) — mirror
        // it onto the header so the stored invoice doc reflects what actually posts,
        // instead of a disconnected default.
        if (catalog?.vendor_payment_terms) setHeader((h) => ({ ...h, payment_terms: catalog.vendor_payment_terms }));
      }).catch(() => setChargeCatalog({ notice: "Finance catalog is unavailable. You can save a local draft, but SAP draft validation remains locked.", gl_mappings: [], hsn_codes: [], tax_codes: [], historical_profiles: [] }));
      if (searchParams.get("prepare") === "1") setStep(2);
  };

  const openPreparation = (item) => navigate(`/invoices/prepare/${encodeURIComponent(item.worklist_id)}`);
  const continueApprovedInvoice = (item) => {
    const params = { source: "notification", reference: item.notification_id, worklist: item.worklist_id, match: item.three_way_match_id, prepare: "1" };
    if (item.materials_hub === "preview") params.hub = "preview";
    navigate(`/invoices/new?${new URLSearchParams(params)}`);
  };
  const restartWithVerifiedReceipt = () => {
    const notificationId = shipmentData?.notification_ids?.[0] || lineItems[0]?.notification_id;
    if (!notificationId) { toast.error("The verified receipt reference is unavailable. Return to the receipt list and prepare S000843 again."); return; }
    navigate(`/invoices/new?${new URLSearchParams({ source: "notification", reference: notificationId })}`);
  };

  // Same Materials Hub scan Prepare already fetches (ThreeWayMatchPanel) —
  // surfaced again here so finance can keep checking the invoice copy on
  // step 2 (inline) and step 3 (via "View invoice copy"), without refetching
  // or revoking the blob URL on every step change. Intentionally NOT gated
  // on `step` — it must survive moving from step 2 to step 3 untouched.
  useEffect(() => {
    const worklistId = searchParams.get("worklist");
    if (!worklistId) return;
    let cancelled = false;
    let objectUrl = null;
    setInvoiceImageState("loading");
    setInvoiceImageUrl(null);
    const hub = shipmentData?.materials_hub === "preview" ? "preview" : "prod";
    apiGetBlob(`/three-way-matches/source-attachment?${new URLSearchParams({ worklist_id: worklistId, hub })}`)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setInvoiceImageUrl(objectUrl);
        setInvoiceImageType(blob.type || "");
        setInvoiceImageState("ready");
      })
      .catch(() => { if (!cancelled) setInvoiceImageState("unavailable"); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("worklist"), shipmentData?.materials_hub]);

  /* ── Step 2: Line item helpers ───────────────────────────── */
  const updateItem = (idx, field, value) => {
    setLineItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === "rejected_qty" && !next[idx].is_landed_cost_line) {
        next[idx].invoiced_qty = Math.max(Number(next[idx].received_qty || 0) - Math.max(Number(value || 0), 0), 0);
      }
      if (field === "hsn_code") {
        next[idx].hsn_source = "manual";
      }
      return next;
    });
  };

  const addLCLine = (chargeType = "Freight", suggestion = null) => {
    const label = chargeType === "Round Off" ? "ROUND OFF" : `${chargeType.toUpperCase()} CHARGES`;
    // HSN pre-fill: prefer a learned historical suggestion (catalog), else
    // for Round Off always "000000". Otherwise (Freight/Packing/Loading/
    // other charges) fall back to the single goods HSN on this invoice when
    // there's exactly one, or "000000" when there are none/multiple distinct
    // ones — these charges attach to whichever delivery they're billed
    // with, so there's no separate SAC lookup needed; this just avoids
    // blocking Continue on a manual HSN entry for charges that don't carry
    // their own meaningful classification.
    const goodsHsns = [...new Set(goodsEntries.map(({ item }) => item.hsn_code).filter((h) => h && String(h).trim()))];
    const defaultHsn = chargeType === "Round Off" ? "000000" : (goodsHsns.length === 1 ? goodsHsns[0] : "000000");
    // Every charge type defaults to "Allocate costs" checked, pre-targeted at
    // every GRN already on this invoice. For Round Off this isn't just a
    // default — it's mandatory (locked, not user-togglable): an unallocated
    // round-off would be an orphaned balancing entry with nowhere to land.
    setLineItems((prev) => [
      ...prev,
      {
        grn_number: shipmentData?.shipment_code || "",
        po_number: "", po_item_number: "10",
        material_code: `LC-${chargeType.toUpperCase().replaceAll(" ", "-")}`, material_description: label,
        uom: suggestion?.uom || "EA", ordered_qty: 1, received_qty: 1, rejected_qty: 0, invoiced_qty: 1,
        unit_price: 0, line_amount: 0,
        hsn_code: suggestion?.hsn_sac || defaultHsn, cgst_rate: 0, sgst_rate: 0, igst_rate: 0,
        cgst_amount: 0, sgst_amount: 0, igst_amount: 0,
        is_interstate: false, is_landed_cost_line: true,
        landed_cost_component: chargeType, charge_type: chargeType, charge_direction: "debit",
        gl_account: suggestion?.gl_account || "", assignment_type: suggestion?.assignment_type || "ACC", cost_center: suggestion?.cost_center || "", tax_code: suggestion?.tax_code || "1", sap_item_type_code: suggestion?.sap_item_type_code || suggestion?.item_type || "",
        allocate_costs: true, allocation_targets: grnTargets, suggestion_source: suggestion?.source?.startsWith("posted_sap_history") ? `posted_sap_history:${suggestion.source_invoice_id}` : suggestion?.source || "",
      },
    ]);
  };

  const removeLine = (idx) => setLineItems((prev) => prev.filter((_, i) => i !== idx));
  const goodsEntries = useMemo(() => lineItems.map((item, index) => ({ item, index })).filter(({ item }) => !item.is_landed_cost_line), [lineItems]);
  const chargeEntries = useMemo(() => lineItems.map((item, index) => ({ item, index })).filter(({ item }) => item.is_landed_cost_line), [lineItems]);
  const grnTargets = useMemo(() => [...new Set(goodsEntries.map(({ item }) => item.grn_number).filter(Boolean))], [goodsEntries]);
  const supplementalValid = chargeEntries.every(({ item }) => Number(item.unit_price) > 0 && item.charge_type && item.charge_direction && item.gl_account && item.hsn_code && item.tax_code && item.uom && (item.assignment_type !== "CC" || item.cost_center) && (!item.allocate_costs || item.allocation_targets?.length));
  const hasSupplementalCharges = chargeEntries.length > 0;

  /* ── Receipt Date preview (auto-derived, read-only) ──────────
     Backend recomputes this authoritatively on save from the verified
     Materials Hub GRN data — this is just a same-logic preview so the
     user can see it before saving. Picks the latest receipt date among
     deliveries actually referenced by this invoice's line items. */
  const previewReceiptDate = useMemo(() => {
    const deliveries = shipmentData?.deliveries || [];
    if (!deliveries.length) return "";
    const lineNotificationIds = new Set(lineItems.map((item) => item.notification_id).filter(Boolean));
    const relevant = lineNotificationIds.size ? deliveries.filter((d) => lineNotificationIds.has(d.notification_id)) : deliveries;
    const pool = relevant.length ? relevant : deliveries;
    const dates = pool.map((d) => (d.receipt_date || "").slice(0, 10)).filter(Boolean).sort();
    return dates.length ? dates[dates.length - 1] : "";
  }, [shipmentData, lineItems]);

  /* ── Totals (memo) ───────────────────────────────────────── */
  const totals = useMemo(() => {
    let subtotal = 0, cgst = 0, sgst = 0, igst = 0;
    lineItems.forEach((item) => {
      const t = calcLineGST(item);
      subtotal += t.base;
      cgst     += t.cgst;
      sgst     += t.sgst;
      igst     += t.igst;
    });
    return {
      subtotal: round2(subtotal),
      cgst_amount: round2(cgst),
      sgst_amount: round2(sgst),
      igst_amount: round2(igst),
      total_tax: round2(cgst + sgst + igst),
      grand_total: round2(subtotal + cgst + sgst + igst),
    };
  }, [lineItems]);
  const sapCalculatedTax = savedInvoice?.sap_calculated_tax_amount;
  // A resumed draft that already progressed past the preflight stage (real
  // SAP draft/posted invoice exists) has, by definition, already had its GST
  // verified by SAP — even on older records where SAP's preflight response
  // didn't echo back a reconciled tax figure (sap_calculated_tax_amount
  // stays unset). Without this, "Verify GST with SAP" would keep showing
  // and re-clicking it 409s (the endpoint only accepts draft/error/
  // sap_preflight_passed), even though the draft is ready for Post/Credit.
  const sapCheckAlreadyDone = !["draft", "error", "sap_preflight_passed", undefined, null].includes(savedInvoice?.status);
  // Editing a charge line (or a goods qty/price, via the locked GRN values
  // this never actually happens, but charges are fully editable) on a draft
  // that already passed SAP Check must invalidate that check client-side
  // too — otherwise the page keeps showing "GST verified" / enables Save
  // SAP Draft against data SAP never actually saw. Compare only the fields
  // that affect what gets sent to SAP, not the whole object (computed tax
  // fields differ harmlessly on every render).
  const chargeFingerprint = (items) => JSON.stringify((items || []).map((li) => [li.unit_price, li.invoiced_qty, li.charge_type, li.charge_direction, li.gl_account, li.tax_code, li.hsn_code, li.uom, li.assignment_type, li.cost_center, li.material_description, li.allocate_costs, li.sap_item_type_code]));
  const linesChangedSinceCheck = Boolean(savedInvoice) && chargeFingerprint(lineItems) !== chargeFingerprint(savedInvoice.line_items);
  const sapTaxConfirmed = !linesChangedSinceCheck && ((sapCalculatedTax !== undefined && sapCalculatedTax !== null) || sapCheckAlreadyDone);
  const displayedTotalTax = sapTaxConfirmed ? Number(sapCalculatedTax ?? savedInvoice?.total_tax ?? totals.total_tax) : null;
  const displayedGrandTotal = sapTaxConfirmed ? round2(totals.subtotal + displayedTotalTax) : null;

  /* ── Save draft ──────────────────────────────────────────── */
  // A resumed draft (reopened from the register/worklist) that hasn't been
  // SAP-checked/posted yet is still editable — every subsequent edit must
  // re-POST so the backend's resume-persist logic (line_items/totals) is
  // actually reached. Once a draft moves past this (a real SAP draft/post
  // attempt happened), its line items are locked to what SAP holds, so the
  // cached object is reused as-is.
  const draftIsEditable = !savedInvoice || ["draft", "error", "sap_preflight_passed"].includes(savedInvoice.status);
  // A real Save SAP Draft / Post attempt has actually happened — vs.
  // ``postedInvoice`` just being set from recovery-on-reopen while the
  // invoice is still a plain local draft/sap_preflight_passed with no SAP
  // document of its own yet. Without this split, reopening an untouched
  // draft showed a misleading red "SAP Draft Needs Reconciliation" card.
  const hasSapDraftResult = Boolean(postedInvoice && (postedInvoice.sap_invoice_id || postedInvoice.sap_error_message || !["draft", "sap_preflight_passed"].includes(postedInvoice.status)));
  const saveDraft = async () => {
    if (savedInvoice && !draftIsEditable) return savedInvoice;
    setSaving(true);
    try {
      const body = {
        shipment_code: shipmentData.shipment_code,
        source_type: shipmentData.source_type,
        source_reference: shipmentData.source_reference,
        currency: shipmentData.currency,
        bilty_number: shipmentData.bilty_number || null,
        vendor_id: shipmentData.vendor_id,
        vendor_name: shipmentData.vendor_name,
        po_number: shipmentData.po_number,
        grn_numbers: grnTargets,
        line_items: lineItems.map((item) => { const tax = calcLineGST(item); return { ...item, line_amount: tax.base, cgst_amount: tax.cgst, sgst_amount: tax.sgst, igst_amount: tax.igst }; }),
        ...header,
        site: shipmentData.site,
        plant_code: shipmentData.plant_code,
        plant_source: shipmentData.plant_source,
        buyer_entity_name: shipmentData.buyer_entity_name,
        worklist_id: searchParams.get("worklist") || null,
        three_way_match_id: threeWayMatchId || null,
        materials_hub: shipmentData.materials_hub === "preview" ? "preview" : "prod",
        ...totals,
      };
      const inv = await apiPost("/invoices", body);
      setSavedInvoice(inv);
      // Same normalization-desync issue as check-sap-draft (tax_code is
      // recomputed server-side here too) — sync local state to match.
      if (Array.isArray(inv.line_items)) setLineItems(inv.line_items);
      toast.success(inv.resumed_existing_draft ? `Resumed existing draft: ${inv.invoice_id}` : `Draft saved: ${inv.invoice_id}`);
      return inv;
    } catch (e) {
      toast.error("Save failed: " + e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  /* ── Save SAP draft ──────────────────────────────────────── */
  const postToSAP = async () => {
    setPosting(true);
    setSapProgress({ stage: "preparing", state: "active", message: "Saving the invoice before SAP submission" });
    let poller = null;
    let cancelled = false;
    let inv = null;
    // Backend returns status=sap_draft_submitting instantly and processes the
    // slow SAP steps (idempotency query, SOAP draft creation, Plant
    // verification, GRN sub-item reconciliation) in a BackgroundTask. Poll
    // until a terminal state is observed or the hard deadline is reached.
    const pollUntilDraftTerminal = async (recoveryId) => {
      const deadline = Date.now() + 180000; // 3 minutes patience for SAP ByD cold calls
      while (Date.now() < deadline) {
        await new Promise((r) => window.setTimeout(r, 1200));
        try {
          const latest = await apiGet(`/invoices/${recoveryId}`);
          if (latest?.sap_progress) setSapProgress(latest.sap_progress);
          const state = latest?.sap_progress?.state;
          const status = latest?.status;
          if (status === "invoice_posted") return latest; // idempotency linked a posted invoice
          if (["sap_draft_pending_review", "sap_draft_grn_linked_review", "sap_draft_reconciliation_required"].includes(status)) {
            return latest;
          }
          // Lock released back to retriable state (SOAP failure in background)
          if (status === "sap_preflight_passed" && (latest?.sap_error_message || state === "error")) {
            return latest;
          }
          if (state === "error" || status === "error") return latest;
        } catch (_) { /* keep polling */ }
      }
      return null;
    };
    try {
      inv = await saveDraft();
      if (!inv) return;
      const syncProgress = async () => {
        if (cancelled) return;
        try {
          const current = await apiGet(`/invoices/${inv.invoice_id}/sap-progress`);
          if (cancelled) return;
          if (current.sap_progress) setSapProgress(current.sap_progress);
        } catch (_) { /* The submission response remains the source of truth if polling is unavailable. */ }
      };
      await syncProgress();
      poller = window.setInterval(syncProgress, 700);
      const initial = await apiPost(`/invoices/${inv.invoice_id}/post`, {});
      let posted = initial;
      // Fire-and-forget: the fast response hands back status=sap_draft_submitting.
      // Keep polling until the background task reaches a terminal state.
      if (initial?.status === "sap_draft_submitting") {
        posted = (await pollUntilDraftTerminal(inv.invoice_id)) || initial;
      }
      cancelled = true;
      if (poller) { window.clearInterval(poller); poller = null; }
      setSapProgress(posted.sap_progress || { stage: "complete", state: "complete", message: "SAP draft is ready for finance review" });
      setPostedInvoice(posted);
      setSavedInvoice(posted);
      if (["sap_draft_pending_review", "sap_draft_grn_linked_review"].includes(posted.status)) {
        toast.success(posted.sap_grn_link_verified ? "SAP draft saved with GRN link: " + posted.sap_invoice_id : "SAP draft saved: " + posted.sap_invoice_id);
      } else if (posted.status === "invoice_posted") {
        toast.success("Linked to posted SAP invoice: " + posted.sap_invoice_id);
      } else if (posted.status === "sap_draft_submitting") {
        toast.info("SAP draft is still processing — refresh in a moment to see the final state");
      } else {
        toast.error("SAP draft needs reconciliation: " + (posted.sap_error_message || "Unknown error"));
      }
    } catch (e) {
      // Network/5xx fallback: the background task may still be running.
      // Poll until a terminal state is observed or the deadline is reached.
      const recoveryId = inv?.invoice_id || savedInvoice?.invoice_id;
      const terminal = recoveryId ? await pollUntilDraftTerminal(recoveryId) : null;
      cancelled = true;
      if (poller) { window.clearInterval(poller); poller = null; }
      if (terminal?.sap_invoice_id && ["sap_draft_pending_review", "sap_draft_grn_linked_review"].includes(terminal.status)) {
        setSapProgress(terminal.sap_progress || { stage: "complete", state: "complete", message: "SAP draft is ready for finance review" });
        setPostedInvoice(terminal);
        setSavedInvoice(terminal);
        toast.success(terminal.sap_grn_link_verified ? "SAP draft saved with GRN link: " + terminal.sap_invoice_id : "SAP draft saved: " + terminal.sap_invoice_id);
      } else if (terminal?.status === "sap_draft_reconciliation_required") {
        setSapProgress(terminal.sap_progress || { stage: "reconciliation", state: "error", message: "SAP could not complete the draft" });
        setPostedInvoice(terminal);
        toast.error("SAP draft needs reconciliation: " + (terminal.sap_error_message || "Unknown error"));
      } else {
        setSapProgress((current) => ({ ...(current || {}), state: "error", message: "SAP draft could not be completed", error: e.message || "The SAP request failed." }));
        toast.error(e.message);
      }
    } finally {
      cancelled = true;
      if (poller) window.clearInterval(poller);
      setPosting(false);
    }
  };

  const reconcileSAPDraft = async () => {
    if (!postedInvoice?.invoice_id) return;
    setPosting(true);
    try {
      const reconciled = await apiPost(`/invoices/${postedInvoice.invoice_id}/reconcile-sap-draft`, {});
      setPostedInvoice(reconciled);
      toast.success(`Located existing SAP draft ${reconciled.sap_invoice_id}. No SAP data was changed.`);
    } catch (e) { toast.error(e.message || "Could not reconcile the existing SAP draft"); }
    finally { setPosting(false); }
  };

  const saveSupplementalDraft = async () => {
    const invoice = await saveDraft();
    if (!invoice) return;
    setSupplementalDraftNotice(`Local draft ${invoice.invoice_id} is saved. SAP was not called because this invoice includes additional charge lines.`);
    toast.info("Local draft saved. Combined SAP draft validation is not available yet.");
  };

  const [sapCheckStartedAt, setSapCheckStartedAt] = useState(null);
  const [sapCheckEndedAt, setSapCheckEndedAt] = useState(null);
  const [, setSapCheckTick] = useState(0);
  useEffect(() => {
    if (!sapChecking) return undefined;
    const id = window.setInterval(() => setSapCheckTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [sapChecking]);
  const sapCheckElapsed = (() => {
    if (!sapCheckStartedAt) return null;
    const end = (sapCheckEndedAt || Date.now());
    const totalSec = Math.max(0, Math.floor((end - sapCheckStartedAt) / 1000));
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  })();

  const checkSAPDraft = async () => {
    if (chargeEntries.some(({ item }) => !item.sap_item_type_code)) {
      toast.error("Every additional charge needs an SAP item type from its approved profile before validation.");
      return;
    }
    setSapChecking(true);
    setSapCheckStartedAt(Date.now());
    setSapCheckEndedAt(null);
    try {
      const invoice = await saveDraft();
      if (!invoice) return;
      const checked = await apiPost(`/invoices/${invoice.invoice_id}/check-sap-draft`, {});
      setSavedInvoice(checked);
      // SAP Check normalizes tax_code (and self-heals any stale credit/debit
      // sign) server-side before persisting. Sync local form state to match
      // so ``linesChangedSinceCheck`` reflects an actual USER edit afterwards
      // instead of immediately re-triggering off the backend's own
      // normalization (which hid the GST summary right after a pass).
      if (Array.isArray(checked.line_items)) setLineItems(checked.line_items);
      const elapsedMs = checked?.sap_preflight_duration_ms;
      const elapsedLabel = Number.isFinite(elapsedMs) ? ` · took ${Math.round(elapsedMs / 1000)}s on SAP` : "";
      setSupplementalDraftNotice(checked.status === "sap_preflight_passed" ? `SAP Check passed. SAP confirmed the tax total${elapsedLabel}; you can now save the review-only SAP draft.` : checked.sap_error_message || "SAP Check did not pass.");
      if (checked.status === "sap_preflight_passed") toast.success(`SAP Check passed${elapsedLabel}. SAP tax reconciled; no SAP invoice created.`);
      else toast.error(checked.sap_error_message || "SAP Check did not pass.");
    } catch (error) {
      toast.error(error.message || "SAP Check could not be completed");
    } finally {
      setSapChecking(false);
      setSapCheckEndedAt(Date.now());
    }
  };

  const finalizeSAPPost = async () => {
    if (!postedInvoice?.invoice_id) return;
    const invoiceId = postedInvoice.invoice_id;
    setFinalizing(true);
    const pollUntilTerminal = async () => {
      // Backend returns status=sap_posting_submitting and processes the SAP
      // post in a BackgroundTask. Poll until a terminal state is observed.
      // For a variance-accepted invoice, the Credit-Memo autopilot runs
      // INLINE right after the invoice itself posts (same background task) —
      // so "invoice_posted" alone is not yet terminal for this flow. Keep
      // polling until variance_credit_chain_status leaves "running" too,
      // otherwise the user sees "posted" while a correcting credit note is
      // still being created/posted server-side with no visibility into it.
      const deadline = Date.now() + 180000;
      while (Date.now() < deadline) {
        await new Promise((r) => window.setTimeout(r, 1500));
        try {
          const latest = await apiGet(`/invoices/${invoiceId}`);
          if (!latest) continue;
          const chainStillRunning = latest.variance_credit_chain_status === "running";
          if (latest.status === "invoice_posted" && !chainStillRunning) return latest;
          if (latest.sap_lifecycle_status_code === "8" && latest.sap_posting_status_code === "3" && !chainStillRunning) return latest;
          if (latest.status === "sap_posting_reconciliation_required" || latest.status === "sap_draft_grn_linked_review") {
            if (latest.sap_error_message || latest.sap_progress?.state === "error") return latest;
          }
          if (latest.status === "error") return latest;
        } catch (_) { /* keep polling */ }
      }
      return null;
    };
    try {
      const initial = await apiPost(`/invoices/${invoiceId}/finalize-sap-post`, {});
      let finalized = initial;
      if (initial?.status === "sap_posting_submitting" || (initial?.status === "invoice_posted" && initial?.variance_credit_chain_status === "running")) {
        finalized = (await pollUntilTerminal()) || initial;
      }
      setPostedInvoice(finalized);
      setSavedInvoice(finalized);
      if (finalized?.status === "invoice_posted" || (finalized?.sap_lifecycle_status_code === "8" && finalized?.sap_posting_status_code === "3")) {
        const chainIds = finalized.variance_credit_chain_summary?.map((c) => c.sap_credit_id).filter(Boolean);
        if (finalized.variance_credit_chain_status === "resolved" && chainIds?.length) {
          toast.success(`SAP invoice posted: ${finalized.sap_invoice_id}. Credit note(s) posted automatically: ${chainIds.join(", ")}`);
        } else if (finalized.variance_credit_chain_status === "variance_accepted_credit_chain_failed" || finalized.variance_credit_chain_error) {
          toast.error(`SAP invoice posted: ${finalized.sap_invoice_id}. The correcting credit note failed — see details below.`);
        } else {
          toast.success(`SAP invoice posted: ${finalized.sap_invoice_id}`);
        }
      } else if (finalized?.sap_error_message) {
        toast.error(finalized.sap_error_message);
      } else if (finalized?.status === "sap_posting_submitting") {
        toast.info("SAP posting is still processing — refresh in a moment to see the final state");
      } else {
        toast.error("SAP could not confirm posting");
      }
    } catch (error) {
      // 502 / network: still poll the background task.
      const finalized = await pollUntilTerminal();
      if (finalized?.status === "invoice_posted" || (finalized?.sap_lifecycle_status_code === "8" && finalized?.sap_posting_status_code === "3")) {
        setPostedInvoice(finalized);
        setSavedInvoice(finalized);
        toast.success(`SAP invoice posted: ${finalized.sap_invoice_id}`);
      } else {
        toast.error(error.message || "SAP posting could not be completed");
      }
    } finally {
      setFinalizing(false);
    }
  };

  /* ── Void the saved SAP draft ────────────────────────────────
     Only for a saved-but-not-yet-posted SAP draft (sap_draft_pending_review
     / sap_draft_grn_linked_review) — the backend (/invoices/{id}/void-sap-draft)
     rejects anything already posted/released/cancelled. Same endpoint and
     response shape as InvoiceHistory.jsx's handleVoidSapDraft. */
  const voidSapDraft = async () => {
    if (!postedInvoice?.invoice_id) return;
    setVoiding(true);
    try {
      const result = await apiPost(`/invoices/${postedInvoice.invoice_id}/void-sap-draft`, {});
      if (result.ok && result.stage === "complete") {
        toast.success(`SAP confirmed ${result.sap_invoice_id} is removed. ${result.worklist_rows_released || 0} GRN(s) released back to Due for Invoicing.`);
        backToRegister();
      } else if (result.stage === "manual_action_required") {
        if (result.sap_ui_link) window.open(result.sap_ui_link, "_blank", "noopener,noreferrer");
        toast.info(`Opened ${result.sap_invoice_id} in SAP ByD. Click Delete there, then press Void SAP Draft again to clean up locally.`, { duration: 8000 });
      } else {
        toast.error(result.message || "SAP did not confirm the void — no local change was made.");
      }
    } catch (e) {
      const detail = e?.detail;
      const message = typeof detail === "object" ? detail?.message : detail;
      toast.error(message || e.message || "SAP draft void failed");
    } finally {
      setVoiding(false);
    }
  };

  /* ── Validation ──────────────────────────────────────────── */
  const varianceAcceptedFlow = Boolean(approvedMatch?.variance_accepted_from_exception);
  // Non-LC goods lines must carry an HSN. Missing HSN causes SAP to fall back
  // to a 0% tax determination silently, so the Verify-GST and Continue
  // transitions are hard-blocked until every goods line is populated.
  const missingHsnGoodsIdx = goodsEntries
    .filter(({ item }) => !item.is_landed_cost_line && !(item.hsn_code && String(item.hsn_code).trim()))
    .map(({ index }) => index);
  const goodsHsnComplete = missingHsnGoodsIdx.length === 0;
  const canProceed = {
    1: !!shipmentData?.receipt_ready && !invoiceExceptionHoldBlocked,
    2: !invoiceExceptionHoldBlocked && goodsEntries.length > 0 && goodsEntries.every(({ line = null, item }) =>
      Number.isFinite(item.unit_price) && item.unit_price > 0 &&
      item.invoiced_qty > 0 && item.rejected_qty >= 0 &&
      // Exact three-way qty equality is enforced for normal invoices. For
      // variance-accepted exceptions the whole intent is to post the billed
      // qty (which differs from accepted GRN qty by design) and rebase the
      // delta via a Credit Note after posting — so the equality is skipped.
      (varianceAcceptedFlow || Math.abs(item.invoiced_qty - (item.received_qty - item.rejected_qty)) < 0.000001)
    ) && supplementalValid && goodsHsnComplete,
    3: !invoiceExceptionHoldBlocked && !!(header.invoice_number && header.invoice_date && header.posting_date) && goodsHsnComplete,
    4: !invoiceExceptionHoldBlocked,
  };

  // When landing on step 1 (the register), render the SAP-style list
  // full-bleed — no stepper, no amber banner, no card chrome. The
  // stepper + banner only appear once the user enters the Create
  // Invoice flow (step 2+).
  if (step === 1) {
    return (
      <div className="min-w-0" data-testid="new-supplier-invoice-page">
        <DueInvoicingWorklist onPrepare={openPreparation} onContinue={continueApprovedInvoice} />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-6 min-h-[calc(100vh-8rem)] lg:flex-row" data-testid="new-supplier-invoice-page">
      {/* ── Stepper sidebar ── */}
      <aside className="w-full shrink-0 lg:w-56">
        {/* SAP-style "Back to register" affordance — shown whenever
            we're inside the Create Invoice flow (URL carries source/
            match/prepare params OR we've advanced past step 1). The
            register itself never renders this. */}
        {(step > 1 || searchParams.get("source") || searchParams.get("match") || searchParams.get("prepare") === "1") && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => backToRegister()}
            className="mb-3 w-full gap-1.5"
            data-testid="new-invoice-back-button"
          >
            <ArrowLeft size={13} /> Back to register
          </Button>
        )}
        {shipmentData?.shipment_code && (
          <div className="mb-3 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="invoice-shipment-code-badge">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Shipment</p>
            <p className="font-mono text-sm font-bold text-slate-900">{shipmentData.shipment_code}</p>
          </div>
        )}
        {shipmentData?.plant_code && (
          <div className="mb-3 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="invoice-site-badge">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Site</p>
            <p className="font-mono text-sm font-bold text-slate-900">{shipmentData.plant_code}{shipmentData.buyer_entity_name && <span className="ml-1 font-sans text-xs font-normal text-slate-400">({shipmentData.buyer_entity_name})</span>}</p>
          </div>
        )}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-2 lg:block lg:space-y-1">
            {STEPS.map(({ id, label }) => {
              const done    = id < step;
              const active  = id === step;
              const locked  = id > step;
              const draftExists = Boolean(postedInvoice?.sap_invoice_id);
              const clickable = done && !draftExists;
              return (
                <div
                  key={id}
                  className={`flex items-start gap-3 ${clickable ? "cursor-pointer group" : ""}`}
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={clickable ? () => setStep(id) : undefined}
                  onKeyDown={clickable ? (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setStep(id); } } : undefined}
                  title={done && draftExists ? "A SAP draft already exists for this invoice — go back only via Void SAP draft." : clickable ? `Back to ${label}` : undefined}
                  data-testid={`step-nav-${id}`}
                >
                  {/* connector */}
                  <div className="flex flex-col items-center">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
                      done   ? `bg-emerald-100 text-emerald-700 ${clickable ? "group-hover:bg-emerald-200" : ""}`
                             : active ? "bg-[#0052CC] text-white"
                             : "bg-slate-100 text-slate-400"
                    }`}>
                      {done ? <CheckCircle size={13} /> : id}
                    </div>
                    {id < STEPS.length && <div className="w-px h-6 bg-slate-100 my-0.5" />}
                  </div>
                  <div className="pt-0.5">
                    <p className={`text-[12px] font-medium ${active ? "text-[#0052CC]" : done ? `text-slate-700 ${clickable ? "group-hover:text-[#0052CC] group-hover:underline" : ""}` : "text-slate-400"}`}>
                      {label}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Status after post */}
        {savedInvoice && (
          <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4 space-y-2">
            <p className="text-[11px] font-mono text-slate-500 uppercase">Invoice</p>
            <p className="text-[13px] font-mono font-semibold text-slate-900">{savedInvoice.invoice_id}</p>
            <StatusBadge status={savedInvoice.status} />
            {savedInvoice.sap_invoice_id && (
              <p className="text-[10px] font-mono text-slate-400">SAP: {savedInvoice.sap_invoice_id}</p>
            )}
          </div>
        )}
      </aside>

      {/* ── Step content ── */}
      <div className="min-w-0 flex-1 bg-white border border-slate-200 rounded-lg">
        {shipmentData?.po_item_fifo_blocked && (
          <section role="alert" className="mx-4 mt-4 border-l-4 border-red-600 bg-red-50 p-4 text-sm text-red-950" data-testid="po-item-fifo-blocker-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-700" />
              <div className="min-w-0 space-y-2 w-full">
                <p className="font-semibold">An earlier, still-open shipment shares a PO + item with this one.</p>
                <p>
                  SAP always consumes open delivery balance oldest-first for a shared PO + item, regardless of which GRN this invoice references.
                  Submitting now would split the billed quantity across both shipments' deliveries, which we can't safely auto-reconcile.
                  Invoice the earliest shipment below first, then come back to create this one.
                </p>
                <ul className="mt-3 space-y-1 text-xs" data-testid="po-item-fifo-blocker-list">
                  {(shipmentData.po_item_fifo_blockers || []).map((blocker, idx) => (
                    <li key={blocker.shipment_code || idx} className="flex flex-wrap items-center gap-2" data-testid={`po-item-fifo-blocker-${blocker.shipment_code}`}>
                      <span>
                        {idx === 0 && <span className="mr-1 rounded bg-red-200 px-1 py-0.5 text-[9px] font-bold uppercase">Earliest</span>}
                        Shipment <span className="font-mono font-semibold">{blocker.shipment_code}</span>
                        {blocker.po_numbers?.length ? ` · PO ${blocker.po_numbers.join(", ")}` : ""}
                        {blocker.receipt_date ? ` · ${fmtDate(blocker.receipt_date)}` : ""}
                      </span>
                      {blocker.shipment_code && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/invoices/prepare/direct-${encodeURIComponent(blocker.shipment_code)}${shipmentData.materials_hub === "preview" ? "?hub=preview" : ""}`)}
                          data-testid={`po-item-fifo-open-${blocker.shipment_code}`}
                        >
                          Open {blocker.shipment_code}
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}
        {approvedMatch?.variance_accepted_from_exception && (() => {
          const goodsLines = lineItems.filter((item) => !item.is_landed_cost_line);
          const billedTotal = goodsLines.reduce((sum, item) => sum + Number(item.invoiced_qty || 0) * Number(item.unit_price || 0), 0);
          const receivedTotal = goodsLines.reduce((sum, item) => sum + Number(item.received_qty || 0) * Number(item.unit_price || 0), 0);
          const varianceAmount = billedTotal - receivedTotal;
          const direction = varianceAmount >= 0 ? "over-billed" : "under-billed";
          const chainStatus = postedInvoice?.variance_credit_chain_status;
          const chainSummary = postedInvoice?.variance_credit_chain_summary || [];
          const isPosted = postedInvoice?.status === "invoice_posted";
          return (
            <section role="alert" className="mx-4 mb-4 border-l-4 border-amber-600 bg-amber-50 p-4 text-sm text-amber-950" data-testid="variance-accepted-banner">
              <div className="flex items-start gap-3">
                <AlertCircle size={18} className="mt-0.5 shrink-0 text-amber-700" />
                <div className="min-w-0 space-y-2 w-full">
                  <p className="font-semibold">Supplier {direction} by {fmtCurrency(Math.abs(varianceAmount))}</p>
                  {!isPosted && (
                    <>
                      <p>
                        The supplier billed {fmtCurrency(billedTotal)} but only {fmtCurrency(receivedTotal)} worth of goods was actually received. We will post the invoice for the <strong>full billed amount</strong> so SAP records the exact invoice the supplier sent.
                      </p>
                      <p>
                        <strong>Right after that invoice posts</strong>, a correcting <strong>Credit Memo / Subsequent Credit</strong> for {fmtCurrency(Math.abs(varianceAmount))} is created and posted <strong>automatically</strong> — no manual step needed. This reduces what we owe the supplier so they are only paid for what was actually received. Stock and inventory values are not affected.
                      </p>
                    </>
                  )}
                  {isPosted && (
                    <div className="space-y-2" data-testid="variance-outcome-detail">
                      <p data-testid="variance-outcome-invoice-posted">
                        Created and posted supplier invoice <span className="font-mono font-semibold">{postedInvoice.sap_invoice_id}</span> for {fmtCurrency(billedTotal)} (full billed amount).
                      </p>
                      {chainStatus === "running" && (
                        <p className="flex items-center gap-2 text-amber-900" data-testid="variance-outcome-chain-running">
                          <Loader2 size={14} className="animate-spin" /> Creating the correcting credit note(s) on SAP now…
                        </p>
                      )}
                      {chainStatus === "resolved" && chainSummary.length > 0 && (
                        <div data-testid="variance-outcome-chain-resolved">
                          <p className="font-semibold">Posted correcting credit note(s):</p>
                          <ul className="mt-1 space-y-1">
                            {chainSummary.map((c, i) => (
                              <li key={c.credit_id || i} className="rounded border border-amber-200 bg-white/70 px-2 py-1 font-mono text-xs" data-testid={`variance-outcome-chain-item-${i}`}>
                                <span className="font-semibold">{c.sap_credit_id}</span> — {c.component}: {fmtCurrency(c.subtotal)} + {fmtCurrency(c.total_tax)} tax = {fmtCurrency(c.grand_total)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {(chainStatus === "no_credit_required" || chainStatus === "already_resolved") && (
                        <p data-testid="variance-outcome-chain-no-credit">
                          No correcting credit note was needed — the billed quantity matched the GRN exactly.
                        </p>
                      )}
                      {(chainStatus === "variance_accepted_credit_chain_failed" || postedInvoice?.variance_credit_chain_error) && (
                        <div className="rounded border border-red-300 bg-red-50 p-2 text-red-900" data-testid="variance-outcome-chain-failed">
                          <p className="font-semibold">The correcting credit note failed to post automatically.</p>
                          <p className="mt-1 text-xs font-mono">{postedInvoice?.variance_credit_chain_error}</p>
                          <p className="mt-1 text-xs">The invoice itself is posted and unaffected — only the credit note needs attention.</p>
                          {chainSummary.some((c) => c.sap_credit_id) ? (
                            <ul className="mt-2 space-y-1">
                              {chainSummary.map((c, i) => (
                                <li key={c.credit_id || i} className="rounded border border-red-200 bg-white/70 px-2 py-1 text-xs" data-testid={`variance-outcome-chain-failed-item-${i}`}>
                                  <p className="font-mono"><span className="font-semibold">{c.sap_credit_id}</span> — {c.component}: {fmtCurrency(c.grand_total)} — {c.status}</p>
                                  {c.sap_ui_link && (
                                    <a href={c.sap_ui_link} target="_blank" rel="noreferrer" className="mt-0.5 inline-block underline text-red-900" data-testid={`variance-outcome-chain-sap-link-${i}`}>
                                      Open {c.sap_credit_id} in SAP to resolve &amp; post
                                    </a>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <>
                              <p className="mt-1 text-xs">Raise it manually below.</p>
                              <Button type="button" size="sm" className="mt-2 bg-amber-700 text-white hover:bg-amber-800" onClick={() => setShowSupplierCredit(true)} data-testid="post-variance-credit-memo-button">
                                Raise Credit Note manually
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-amber-800">Linked to exception <span className="font-mono">{approvedMatch.variance_accepted_from_exception}</span></p>
                </div>
              </div>
            </section>
          );
        })()}
        {invoiceExceptionHoldBlocked && (
          <section role="alert" className="mx-4 mb-4 border-l-4 border-red-600 bg-red-50 p-4 text-sm text-red-950" data-testid="invoice-exception-hold-banner">
            <div className="flex items-start gap-3">
              <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-700" />
              <div className="min-w-0">
                <p className="font-semibold">Materials Hub already recorded an invoice or credit note against this shipment.</p>
                <p className="mt-1">Posting another invoice would double-book. Reconcile the upstream document first before continuing.</p>
                <ul className="mt-3 space-y-1 text-xs" data-testid="invoice-exception-hold-list">
                  {invoiceExceptionHolds.map((entry, idx) => {
                    const hold = entry?.hold || {};
                    const refs = Object.entries(hold)
                      .filter(([, v]) => v != null && v !== "")
                      .map(([k, v]) => `${k.replaceAll("_", " ")}: ${v}`)
                      .join(" · ");
                    return (
                      <li key={`${entry.po_number || "po"}-${entry.po_item_number || "line"}-${idx}`} className="flex flex-wrap items-center gap-2" data-testid={`invoice-exception-hold-${idx}`}>
                        <span>PO <span className="font-mono font-semibold">{entry.po_number || "-"}</span> · Item <span className="font-mono">{entry.po_item_number || "-"}</span>{entry.material_code ? ` · ${entry.material_code}` : ""}{refs ? ` · ${refs}` : ""}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </section>
        )}
        {/* Step 1 */}
        {step === 1 && (
          <StepShell title="Invoice source" description="Find the receipt in the due-for-invoicing table to begin preparation.">
            <DueInvoicingWorklist onPrepare={openPreparation} onContinue={continueApprovedInvoice} />
          </StepShell>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <StepShell title="Review GRN Line Items" description="Exact three-way matching has locked accepted quantities and PO rates. Add supplementary charges only when applicable.">
            {invoiceImageState === "unavailable" && <p className="mb-4 flex items-center gap-1 text-xs text-slate-500" data-testid="invoice-copy-unavailable"><ImageOff size={13} />No invoice copy from Materials Hub yet</p>}
            {invoiceImageState === "ready" && (
              <div className="mb-5 h-72 sm:h-96" data-testid="invoice-copy-panel">
                {invoiceImageType === "application/pdf"
                  ? <PdfAttachment src={invoiceImageUrl} />
                  : <InvoiceImageZoom src={invoiceImageUrl} alt={`Supplier invoice ${header.invoice_number || shipmentData?.shipment_code || ""}`} />}
              </div>
            )}
            <div className="hidden overflow-x-auto xl:block">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="border-b-2 border-slate-300 bg-slate-200">
                    {["GRN / PO Item", "Material", "HSN / SAC", "UOM", "Ordered", "Received", "Invoiced Qty", "Unit Price", "Line Amt", "LC?", ""].map((h) => (
                      <th key={h} className="px-3 py-2.5 text-left font-bold text-slate-700 uppercase text-[11px] tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-300">
                  {goodsEntries.map(({ item, index: idx }) => (
                    <tr key={idx} className={`hover:bg-slate-50 ${item.is_landed_cost_line ? "bg-amber-50/40" : ""}`}>
                      <td className="px-3 py-2 font-mono text-slate-700">{item.grn_number}</td>
                      <td className="px-3 py-2 text-slate-800 max-w-[160px] truncate">{item.material_description}</td>
                      <td className="px-3 py-2">
                        {item.is_landed_cost_line ? (
                          <span className="font-mono text-xs text-slate-500">{item.hsn_code || "—"}</span>
                        ) : (
                          <div className="flex items-center gap-1">
                            <Input
                              type="text"
                              value={item.hsn_code || ""}
                              onChange={(e) => updateItem(idx, "hsn_code", e.target.value.trim())}
                              placeholder="HSN"
                              className={`h-7 w-24 font-mono text-xs ${!(item.hsn_code && String(item.hsn_code).trim()) ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                              maxLength={10}
                              data-testid={`goods-line-hsn-${idx}`}
                            />
                            {!(item.hsn_code && String(item.hsn_code).trim()) && (
                              <span
                                className="font-mono text-[9px] px-1 py-0.5 rounded uppercase bg-red-100 text-red-700"
                                title="HSN is required. SAP falls back to 0% tax if HSN is missing."
                                data-testid={`goods-line-hsn-missing-${idx}`}
                              >HSN missing</span>
                            )}
                            {item.hsn_source && (
                              <span
                                className={`font-mono text-[9px] px-1 py-0.5 rounded uppercase ${
                                  item.hsn_source === "grn"
                                    ? "bg-emerald-100 text-emerald-700"
                                    : item.hsn_source === "sap_master"
                                    ? "bg-teal-100 text-teal-700"
                                    : item.hsn_source === "material_master"
                                    ? "bg-sky-100 text-sky-700"
                                    : item.hsn_source === "manual"
                                    ? "bg-amber-100 text-amber-700"
                                    : "bg-slate-200 text-slate-600"
                                }`}
                                title={
                                  item.hsn_source === "grn"
                                    ? "Pulled from GRN upstream"
                                    : item.hsn_source === "sap_master"
                                    ? "Pulled live from SAP (Business Analytics HSN report)"
                                    : item.hsn_source === "material_master"
                                    ? "Pulled from Material Master (GRN did not carry HSN)"
                                    : item.hsn_source === "manual"
                                    ? "Overridden manually on this invoice"
                                    : "Fallback default"
                                }
                                data-testid={`goods-line-hsn-source-${idx}`}
                              >
                                {item.hsn_source === "sap_master" ? "SAP" : item.hsn_source === "material_master" ? "Master" : item.hsn_source}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-slate-600">{item.uom}</td>
                      <td className="px-3 py-2 font-mono">{item.ordered_qty}</td>
                      <td className="px-3 py-2 font-mono">{item.received_qty}</td>
                      <td className="px-3 py-2">
                        <span className="font-mono text-slate-700" data-testid={`invoiced-qty-${idx}`}>{item.invoiced_qty}</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-slate-700" data-testid={`unit-price-${idx}`}>{item.unit_price}</td>
                      <td className="px-3 py-2 font-mono text-slate-700">{fmtCurrency(item.invoiced_qty * item.unit_price)}</td>
                      <td className="px-3 py-2">
                        {item.is_landed_cost_line && <span className="text-[10px] font-mono bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">LC</span>}
                      </td>
                      <td className="px-3 py-2">
                        {item.is_landed_cost_line && (
                          <Button variant="ghost" size="sm" onClick={() => removeLine(idx)} disabled={chargesLocked} className="h-6 w-6 p-0 text-red-400 hover:text-red-600" data-testid={`remove-invoice-line-${idx}`}>
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="space-y-3 xl:hidden" data-testid="invoice-lines-mobile">
              {goodsEntries.map(({ item, index: idx }) => <article key={idx} className="space-y-3 border border-slate-200 p-3" data-testid={`invoice-line-mobile-${idx}`}>
                <p className="break-words text-sm font-bold" data-testid={`invoice-line-mobile-description-${idx}`}>{item.material_description}</p>
                <p className="break-words font-mono text-xs text-slate-500" data-testid={`invoice-line-mobile-reference-${idx}`}>GRN {item.grn_number} · PO {item.po_number} / {item.po_item_number}</p>
                <p className="text-xs text-slate-600" data-testid={`invoice-line-mobile-quantity-${idx}`}>Received {item.received_qty} {item.uom} · Ordered {item.ordered_qty}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-xs text-slate-600">Invoice quantity<p className="mt-2 font-mono text-sm text-slate-800" data-testid={`invoiced-qty-mobile-${idx}`}>{item.invoiced_qty}</p></div>
                  <div className="text-xs text-slate-600">Unit price<p className="mt-2 font-mono text-sm text-slate-800" data-testid={`unit-price-mobile-${idx}`}>{item.unit_price}</p></div>
                </div>
                {!item.is_landed_cost_line && (
                  <div className="text-xs text-slate-600">
                    HSN / SAC
                    <div className="mt-2 flex items-center gap-1">
                      <Input
                        type="text"
                        value={item.hsn_code || ""}
                        onChange={(e) => updateItem(idx, "hsn_code", e.target.value.trim())}
                        placeholder="HSN"
                        className={`h-7 w-28 font-mono text-xs ${!(item.hsn_code && String(item.hsn_code).trim()) ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                        maxLength={10}
                        data-testid={`goods-line-hsn-mobile-${idx}`}
                      />
                      {!(item.hsn_code && String(item.hsn_code).trim()) && (
                        <span className="font-mono text-[9px] px-1 py-0.5 rounded uppercase bg-red-100 text-red-700" data-testid={`goods-line-hsn-missing-mobile-${idx}`}>HSN missing</span>
                      )}
                      {item.hsn_source && (
                        <span className={`font-mono text-[9px] px-1 py-0.5 rounded uppercase ${
                          item.hsn_source === "grn" ? "bg-emerald-100 text-emerald-700" :
                          item.hsn_source === "sap_master" ? "bg-teal-100 text-teal-700" :
                          item.hsn_source === "material_master" ? "bg-sky-100 text-sky-700" :
                          item.hsn_source === "manual" ? "bg-amber-100 text-amber-700" :
                          "bg-slate-200 text-slate-600"
                        }`}>{item.hsn_source === "sap_master" ? "SAP" : item.hsn_source === "material_master" ? "Master" : item.hsn_source}</span>
                      )}
                    </div>
                  </div>
                )}
                <p className="text-sm font-bold" data-testid={`invoice-line-mobile-amount-${idx}`}>{fmtCurrency(item.invoiced_qty * item.unit_price)}</p>
                {item.is_landed_cost_line && <Button type="button" variant="outline" size="sm" onClick={() => removeLine(idx)} disabled={chargesLocked} data-testid={`remove-invoice-line-mobile-${idx}`}><Trash2 size={14} /> Remove charge</Button>}
              </article>)}
            </div>
            <SupplementalChargeEditor charges={chargeEntries} onAdd={addLCLine} onChange={(index, field, value) => updateItem(index, field, value)} onRemove={removeLine} catalog={chargeCatalog} grnTargets={grnTargets} vendorId={shipmentData?.vendor_id} currency={shipmentData?.currency} locked={chargesLocked} />
            {!threeWayMatchId && <p role="alert" className="mt-4 border-l-2 border-amber-600 bg-amber-50 p-3 text-sm text-amber-950" data-testid="three-way-match-required">A full exact three-way match is required before invoice details, SAP Check, or SAP draft creation. Start from the SAP-checked worklist’s Prepare SAP Draft action.</p>}
            <div className="mt-5 p-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <Button variant="outline" size="sm" onClick={() => addLCLine("Freight", chargeCatalog?.freight_profile || null)} disabled={chargesLocked} className="gap-2 text-[12px] border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="add-lc-line-button">
                <Plus size={13} /> Add freight charge
              </Button>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="step2-back-button" className="text-[13px]">Back</Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button disabled={!canProceed[2] || !threeWayMatchId} title={!goodsHsnComplete ? `Add HSN on all goods lines (${missingHsnGoodsIdx.length} missing)` : threeWayMatchId ? "Continue after exact three-way approval" : "Start from Prepare SAP Draft and approve the exact three-way match first"} data-testid="step2-next-button" className="bg-[#0052CC] hover:bg-[#0043A8] text-white gap-2 text-[13px]">
                      Continue <ChevronRight size={14} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="lock-charges-confirm-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Lock charges and continue?</AlertDialogTitle>
                      <AlertDialogDescription>After this no additional charges can be added to the invoice. Please confirm.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="lock-charges-cancel-button">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => { setChargesLocked(true); setStep(3); }} data-testid="lock-charges-confirm-button">Confirm</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </StepShell>
        )}

        {/* Step 3 — Invoice Details on the left + SAP Draft & Post on the right,
            everything on one page. Grid is centered within the content area
            and each panel's content fills its full column width. */}
        {(step === 3 || step === 4) && (
        <div className="mx-auto grid w-full grid-cols-1 xl:grid-cols-2 xl:divide-x xl:divide-slate-100">
        {step === 3 && (
          <StepShell
            title="Invoice Details & GST"
            description={threeWayMatchId ? "Invoice number is locked from the Materials Hub GRN." : "Enter supplier invoice number, date, and GST information."}
            headerAction={invoiceImageState === "ready" ? (
              <Button type="button" variant="outline" size="sm" className="h-8 shrink-0 gap-1.5 text-[12px]" onClick={() => setInvoiceCopyModalOpen(true)} data-testid="view-invoice-copy-button">
                <Eye size={14} /> View invoice copy
              </Button>
            ) : invoiceImageState === "loading" ? (
              <span className="flex shrink-0 items-center gap-1.5 text-[11px] text-slate-400" data-testid="view-invoice-copy-loading"><Loader2 size={13} className="animate-spin" />Loading invoice copy…</span>
            ) : undefined}
          >
            <p className="mb-4 text-xs font-bold text-slate-600" data-testid="supplier-invoice-currency">Invoice currency: {shipmentData?.currency}</p>
            {!goodsHsnComplete && (
              <div className="mb-4 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-[12px] text-red-800" data-testid="hsn-missing-banner">
                <span className="font-mono text-[10px] uppercase font-bold">HSN missing</span>
                <span>
                  {missingHsnGoodsIdx.length} goods line{missingHsnGoodsIdx.length === 1 ? "" : "s"} ha{missingHsnGoodsIdx.length === 1 ? "s" : "ve"} no HSN. SAP falls back to 0% tax when HSN is empty — add HSN on every goods line in Step 2 before running SAP Verify.
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-slate-700">Supplier Invoice Number *</label>
                <Input
                  value={header.invoice_number}
                  onChange={(e) => setHeader((h) => ({ ...h, invoice_number: e.target.value }))}
                  readOnly={Boolean(threeWayMatchId)}
                  aria-readonly={Boolean(threeWayMatchId)}
                  placeholder="e.g. VENDOR/2026/001"
                  className="h-8 text-xs font-mono border-slate-300"
                  data-testid="invoice-number-input"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-slate-700">Supplier Invoice Date *</label>
                <DatePickerInput
                  value={header.invoice_date}
                  onChange={(v) => setHeader((h) => ({ ...h, invoice_date: v }))}
                  data-testid="invoice-date-input"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-slate-700">Payment Terms <span className="font-normal text-slate-400">(from SAP vendor master — not user-editable)</span></label>
                <Input
                  value={chargeCatalog ? (chargeCatalog.vendor_payment_terms || "Not set on this vendor in SAP") : "Loading from SAP…"}
                  readOnly
                  aria-readonly="true"
                  className="h-8 text-xs border-slate-300 bg-slate-50 text-slate-600"
                  data-testid="payment-terms-display"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-slate-700">Receipt Date</label>
                <DatePickerInput
                  value={previewReceiptDate}
                  onChange={() => {}}
                  disabled
                  title="Auto-derived from the Materials Hub GRN receipt date — not editable"
                  data-testid="receipt-date-display"
                  className="bg-slate-50 text-slate-500"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-slate-700">Posting Date *</label>
                <DatePickerInput
                  value={header.posting_date}
                  onChange={(v) => setHeader((h) => ({ ...h, posting_date: v }))}
                  disabled={Boolean(savedInvoice)}
                  title={savedInvoice ? "Draft already saved — Posting Date is locked" : undefined}
                  className={savedInvoice ? "bg-slate-50 text-slate-500" : ""}
                  data-testid="posting-date-input"
                />
                <p className="text-[10px] text-slate-400">Defaults to the Supplier Invoice Date. Backdate into the prior month only if that fiscal period is still open in SAP.</p>
              </div>
            </div>

            {/* Tax summary — spans full column width to the divider */}
            <div className="mt-5 bg-slate-50 border border-slate-200 rounded-lg p-3 w-full" data-testid="gst-summary">
              <p className="text-[10px] font-mono uppercase text-slate-400 mb-2 tracking-wider">GST Summary</p>
              {sapTaxConfirmed ? [["Subtotal", totals.subtotal], ["SAP confirmed tax", displayedTotalTax]].map(([label, val]) => (
                <div key={label} className="flex justify-between py-0.5 text-[12px]">
                  <span className="text-slate-500">{label}</span>
                  <span className="font-mono text-slate-900" data-testid={`gst-summary-${label.toLowerCase().replaceAll(" ", "-")}`}>{fmtCurrency(val)}</span>
                </div>
              )) : <><div className="flex justify-between py-0.5 text-[12px]"><span className="text-slate-500">Subtotal</span><span className="font-mono text-slate-900" data-testid="gst-summary-subtotal">{fmtCurrency(totals.subtotal)}</span></div><div className="flex justify-between py-0.5 text-[12px]"><span className="text-slate-500">Tax</span><span className="font-medium text-amber-800" data-testid="gst-summary-tax-pending">Pending SAP Check</span></div><p className="mt-1 text-[10px] text-amber-800" data-testid="gst-summary-tax-pending-note">Tax is confirmed by SAP Check before a draft can be created.</p></>}
              <div className="border-t border-slate-300 mt-1.5 pt-1.5 flex justify-between text-[13px] font-semibold">
                <span className="text-slate-900">Grand Total</span>
                <span className="font-mono text-[#0052CC]" data-testid="gst-summary-grand-total">{sapTaxConfirmed ? fmtCurrency(displayedGrandTotal) : "Pending SAP Check"}</span>
              </div>
              {savedInvoice?.withholding_tax_resolution && (
                <div className="border-t border-slate-200 mt-1.5 pt-1.5 text-[11px]" data-testid="gst-summary-wht">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-500">Withholding Tax (Sec 194Q) — our estimate</span>
                    <span className={`font-mono font-medium ${savedInvoice.withholding_tax_resolution.code ? "text-indigo-700" : "text-slate-500"}`} data-testid="gst-summary-wht-code">
                      {fmtWhtCode(savedInvoice.withholding_tax_resolution.code)}
                    </span>
                  </div>
                  <p className="mt-0.5 text-slate-400" data-testid="gst-summary-wht-reason">{savedInvoice.withholding_tax_resolution.reason}</p>
                  <p className="mt-0.5 text-slate-400" data-testid="gst-summary-wht-disclaimer">Based on our own FY purchase tracking — SAP's vendor master classification controls what is actually deducted and may differ. Verify in SAP.</p>
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => { if (draftIsEditable) setChargesLocked(false); setStep(2); }} data-testid="step3-back-button" className="text-[12px] h-8">{draftIsEditable ? "Back to edit charges" : "Back"}</Button>
              {!sapTaxConfirmed && <Button onClick={checkSAPDraft} disabled={!canProceed[3] || sapChecking || saving || posting || finalizing} data-testid="verify-gst-with-sap-button" title={!goodsHsnComplete ? `Add HSN on all goods lines before SAP verify (${missingHsnGoodsIdx.length} missing)` : undefined} className="h-8 bg-amber-700 hover:bg-amber-800 text-white gap-2 text-[12px]">{sapChecking ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}Verify GST with SAP{sapChecking && sapCheckElapsed ? <span className="ml-1 font-mono tabular-nums text-[11px] opacity-90">{sapCheckElapsed}</span> : null}</Button>}
              {sapTaxConfirmed && <span className="inline-flex h-8 items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 text-[12px] font-medium text-emerald-800" data-testid="verify-gst-passed-badge"><CheckCircle size={13} />GST verified — use "Save SAP Draft" on the right</span>}
            </div>
          </StepShell>
        )}

        <StepShell title="SAP Draft & Post" description="Save a GRN-linked draft, review it, then explicitly post through SAP.">
            <div className="max-w-lg space-y-4">
              {/* Summary card */}
              <div className="border border-slate-200 rounded-lg p-4 space-y-2 text-[13px]">
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <dt className="text-slate-400">Vendor</dt>
                  <dd className="font-medium">{shipmentData?.vendor_name}</dd>
                  <dt className="text-slate-400">Site</dt>
                  <dd className="font-mono" data-testid="summary-site-id">{shipmentData?.plant_code || shipmentData?.site || "—"}{shipmentData?.buyer_entity_name && <span className="ml-1 text-slate-400">({shipmentData.buyer_entity_name})</span>}</dd>
                  <dt className="text-slate-400">Invoice No.</dt>
                  <dd className="font-mono">{header.invoice_number || "—"}</dd>
                  <dt className="text-slate-400">Date</dt>
                  <dd className="font-mono" data-testid="summary-invoice-date">{fmtDate(header.invoice_date)}</dd>
                  <dt className="text-slate-400">Receipt Date</dt>
                  <dd className="font-mono" data-testid="summary-receipt-date">{fmtDate(previewReceiptDate) || "—"}</dd>
                  <dt className="text-slate-400">Posting Date</dt>
                  <dd className="font-mono" data-testid="summary-posting-date">{fmtDate(header.posting_date)}</dd>
                  <dt className="text-slate-400">Invoicing Unit</dt>
                  <dd className="font-mono">{shipmentData?.site}-FIN</dd>
                  <dt className="text-slate-400">Lines</dt>
                  <dd className="font-mono">{lineItems.length}</dd>
                  <dt className="text-slate-400">Grand Total</dt>
                  <dd className="font-mono font-bold text-[#0052CC]" data-testid="sap-draft-grand-total">{sapTaxConfirmed ? fmtCurrency(displayedGrandTotal) : "Pending SAP Check"}</dd>
                </dl>
              </div>
              <div role="status" className={`border-l-2 p-3 text-xs ${sapTaxConfirmed ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-amber-500 bg-amber-50 text-amber-900"}`} data-testid="sap-draft-check-required">
                <p className={`font-semibold ${sapTaxConfirmed ? "text-emerald-900" : ""}`} data-testid="sap-check-status-text">{sapTaxConfirmed ? `SAP Check passed · Tax verified at ${fmtCurrency(displayedTotalTax)}` : "SAP Check required before saving a draft."}</p>
                {hasSupplementalCharges && !sapTaxConfirmed && <p className="mt-2">Additional charge lines need approved SAP mappings before the check can run.</p>}
                {chargeEntries.some(({ item }) => !item.sap_item_type_code) && <p className="mt-2 font-medium text-amber-950" data-testid="supplemental-sap-profile-required">Each charge needs its approved SAP item type profile before SAP Check can run.</p>}
                {supplementalDraftNotice && <div className="mt-2" data-testid="supplemental-sap-draft-status"><p className="font-medium text-amber-950">{supplementalDraftNotice}</p>{supplementalDraftNotice.includes("Plant") && <Button type="button" size="sm" variant="outline" onClick={restartWithVerifiedReceipt} className="mt-2 border-amber-700 text-amber-950" data-testid="restart-verified-receipt-button">Start again with verified receipt</Button>}</div>}
              </div>
              {hasSupplementalCharges && <section className="border border-slate-200 bg-white p-4" data-testid="supplemental-mapping-review"><div className="flex flex-wrap items-start justify-between gap-3"><div><p className="font-mono text-[10px] font-semibold uppercase text-[#076D74]">Required approval before SAP action</p><h3 className="mt-1 text-sm font-semibold text-slate-950">Supplementary line mapping review</h3><p className="mt-1 text-xs text-slate-600">Review the exact ledger and tax treatment below before running SAP Check or saving a draft.</p></div><span className="border border-slate-300 px-2 py-1 text-[10px] font-semibold text-slate-700" data-testid="supplemental-mapping-review-status">{chargeEntries.every(({ item }) => item.gl_account && item.tax_code && item.hsn_code && item.uom && item.sap_item_type_code) ? "Mapping complete" : "Mapping incomplete"}</span></div><div className="mt-4 space-y-3">{chargeEntries.map(({ item, index }) => <article className="border-l-2 border-[#0A838A] bg-slate-50 p-3 text-xs" key={index} data-testid={`supplemental-mapping-row-${index}`}><div className="flex flex-wrap justify-between gap-2"><p className="font-semibold text-slate-950">{item.charge_type} · {item.material_description} · INR {Number(item.unit_price || 0).toFixed(2)} · {item.charge_direction}</p><p className="font-mono text-slate-600">SAP item type: {item.sap_item_type_code || "Missing"}</p></div><div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4"><p><span className="text-slate-500">G/L</span><br /><strong data-testid={`supplemental-mapping-gl-${index}`}>{item.gl_account || "Missing"}</strong></p><p><span className="text-slate-500">Tax code</span><br /><strong data-testid={`supplemental-mapping-tax-${index}`}>{item.tax_code || "Missing"}</strong></p><p><span className="text-slate-500">HSN / SAC</span><br /><strong data-testid={`supplemental-mapping-hsn-${index}`}>{item.hsn_code || "Missing"}</strong></p><p><span className="text-slate-500">Assignment / CC</span><br /><strong>{item.assignment_type || "Missing"}{item.cost_center ? ` · ${item.cost_center}` : ""}</strong></p></div></article>)}</div></section>}
              <SapDraftProgress progress={sapProgress} />

              <div className="flex flex-wrap items-center gap-3 pt-2" data-testid="primary-action-row">
                <Button
                  variant="outline"
                  onClick={saveDraft}
                  disabled={saving || posting || finalizing || !draftIsEditable}
                  data-testid="save-draft-button"
                  className="text-[13px] gap-2"
                >
                  {saving ? <Loader2 size={13} className="animate-spin" /> : null}
                  Save Draft
                </Button>
                <Button onClick={postToSAP} disabled={invoiceExceptionHoldBlocked || !shipmentData?.receipt_ready || posting || saving || finalizing || savedInvoice?.status !== "sap_preflight_passed" || linesChangedSinceCheck} title={invoiceExceptionHoldBlocked ? "Upstream Materials Hub already recorded an invoice/credit note for this shipment" : shipmentData?.po_item_fifo_blocked ? "Invoice the earlier open shipment(s) above first" : linesChangedSinceCheck ? "Charges changed since the last SAP Check — run Verify GST with SAP again" : savedInvoice?.status === "sap_preflight_passed" ? "Save the SAP draft after a successful SAP Check" : "Run SAP Check (Verify GST with SAP on the left) successfully first"} data-testid="save-sap-draft-button" className="bg-[#0052CC] hover:bg-[#0043A8] text-white gap-2 text-[13px]">{posting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}Save SAP Draft</Button>
                {postedInvoice?.status === "sap_draft_grn_linked_review" && (
                  <Button
                    onClick={finalizeSAPPost}
                    disabled={finalizing || posting || saving || voiding}
                    data-testid="post-sap-invoice-button"
                    className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 text-[13px]"
                  >
                    {finalizing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                    Post Invoice
                  </Button>
                )}
                {postedInvoice?.status === "sap_posting_reconciliation_required" && (
                  <Button
                    onClick={finalizeSAPPost}
                    disabled={finalizing || posting || saving}
                    data-testid="retry-sap-posting-button"
                    className="bg-amber-700 hover:bg-amber-800 text-white gap-2 text-[13px]"
                  >
                    {finalizing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                    Retry SAP posting
                  </Button>
                )}
                {postedInvoice?.status === "sap_draft_reconciliation_required" && <Button onClick={reconcileSAPDraft} disabled={posting || saving || finalizing} data-testid="reconcile-sap-draft-button" className="border border-amber-700 bg-white text-amber-900 hover:bg-amber-50 gap-2 text-[13px]"><Search size={13} />Reconcile existing SAP draft</Button>}
              </div>

              {/* Secondary / corrective actions — deliberately split from the
                  primary save/post flow above with a divider, instead of
                  relying on flex-wrap to decide where this row breaks. */}
              {(["sap_draft_pending_review", "sap_draft_grn_linked_review", "invoice_posted"].includes(postedInvoice?.status) || (postedInvoice?.sap_invoice_id && ["invoice_posted", "sap_draft_pending_review", "sap_draft_grn_linked_review"].includes(postedInvoice.status))) && (
                <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-3" data-testid="secondary-action-row">
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-wide text-slate-400">Other actions</span>
                  {["sap_draft_pending_review", "sap_draft_grn_linked_review"].includes(postedInvoice?.status) && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          disabled={finalizing || posting || saving || voiding}
                          data-testid="void-sap-draft-button"
                          title="Voids this unposted SAP draft and releases the GRN back to Due for Invoicing. Already-posted invoices cannot be voided from here."
                          className="border border-red-700 bg-white text-red-800 hover:bg-red-50 gap-2 text-[13px]"
                        >
                          {voiding ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />}
                          Void SAP Draft
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="void-sap-draft-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Void SAP draft {postedInvoice?.sap_invoice_id}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This removes the unposted draft from SAP Business ByDesign and releases its GRN(s) back to Due for Invoicing. Already-posted invoices cannot be voided this way. This cannot be undone from here.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="void-sap-draft-cancel">Keep draft</AlertDialogCancel>
                          <AlertDialogAction onClick={voidSapDraft} data-testid="void-sap-draft-confirm" className="bg-red-700 hover:bg-red-800">
                            Void SAP draft
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  {postedInvoice?.sap_invoice_id && postedInvoice.status === "invoice_posted" && !["resolved", "no_credit_required", "already_resolved"].includes(postedInvoice.variance_credit_chain_status) && !(postedInvoice.variance_credit_chain_summary || []).some((c) => c.sap_credit_id) && (
                    <Button
                      variant="outline"
                      onClick={() => setShowSupplierCredit(true)}
                      disabled={postedInvoice.variance_credit_chain_status === "running"}
                      className="border-amber-700 text-amber-800 hover:bg-amber-50 text-[13px] gap-2"
                      data-testid="raise-supplier-credit-button"
                      title={postedInvoice.variance_credit_chain_status === "running" ? "A correcting credit note is already being created automatically for this invoice" : undefined}
                    >
                      Raise Credit / Subsequent Credit
                    </Button>
                  )}
                  {["sap_draft_pending_review", "sap_draft_grn_linked_review", "invoice_posted"].includes(postedInvoice?.status) && (
                    <Button variant="outline" onClick={() => backToRegister()} className="ml-auto text-[13px]" data-testid="go-to-history-button">
                      Back to Register
                    </Button>
                  )}
                </div>
              )}

              {/* SAP exceptions / post result — always-reserved panel so SAP
                  warnings, blockers, and the Post Invoice outcome surface in a
                  consistent location below the action buttons. */}
              <section
                className={`min-h-[160px] rounded-lg border p-4 text-[13px] ${
                  hasSapDraftResult
                    ? ["sap_draft_pending_review", "sap_draft_grn_linked_review", "invoice_posted"].includes(postedInvoice.status)
                      ? "border-emerald-200 bg-emerald-50"
                      : "border-red-300 bg-red-50"
                    : "border-dashed border-slate-200 bg-slate-50/50"
                }`}
                data-testid="sap-exceptions-panel"
                aria-live="polite"
              >
                <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500" data-testid="sap-exceptions-panel-title">SAP exceptions &amp; post result</p>
                {!hasSapDraftResult ? (
                  <p className="mt-3 text-xs text-slate-500" data-testid="sap-exceptions-panel-empty">
                    SAP warnings, blockers, and the Post Invoice outcome will appear here after you save the SAP draft or post.
                  </p>
                ) : (
                  <div className="mt-2 flex items-start gap-3" data-testid="sap-draft-result">
                    {["sap_draft_pending_review", "sap_draft_grn_linked_review", "invoice_posted"].includes(postedInvoice.status)
                      ? <CheckCircle size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                      : <AlertCircle size={16} className="text-red-600 shrink-0 mt-0.5" />
                    }
                    <div className="min-w-0">
                      <p className="font-semibold">{postedInvoice.status === "invoice_posted" ? "SAP Invoice Posted" : postedInvoice.status === "sap_draft_grn_linked_review" ? "SAP Draft Saved · GRN Linked" : postedInvoice.status === "sap_draft_pending_review" ? "SAP Draft Saved" : "SAP Draft Needs Reconciliation"}</p>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2"><p className="text-[12px] font-mono break-all">{postedInvoice.sap_invoice_id || postedInvoice.sap_error_message}</p><SapLifecycleBadge invoice={postedInvoice} testId="sap-draft-lifecycle-badge" /></div>
                      {isPeriodClosedError(postedInvoice.sap_error_message) && (
                        <div className="mt-2 flex items-start gap-2 border border-amber-300 bg-amber-50 p-2.5 rounded" data-testid="sap-period-closed-callout">
                          <CalendarClock size={15} className="text-amber-700 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-[12px] font-semibold text-amber-900">Fiscal Period Closed</p>
                            <p className="mt-0.5 text-[11px] text-amber-900">This Posting Date falls in a fiscal period SAP has already closed. Contact Finance/your SAP administrator to temporarily reopen the prior period, or create a corrected draft with a date in the currently open period.</p>
                          </div>
                        </div>
                      )}
                      {postedInvoice.sap_warnings?.length > 0 && (
                        <p role="alert" className="mt-2 text-[11px] text-amber-800" data-testid="sap-draft-warnings">
                          SAP review: {postedInvoice.sap_warnings.join(" · ")}
                        </p>
                      )}
                      {postedInvoice.sap_error_message && postedInvoice.sap_ui_link && (
                        <a href={postedInvoice.sap_ui_link} target="_blank" rel="noreferrer" className="mt-2 inline-block text-[11px] underline text-red-700" data-testid="sap-draft-error-ui-link">
                          Open {postedInvoice.sap_invoice_id} in SAP to resolve
                        </a>
                      )}
                      {postedInvoice.status === "sap_draft_reconciliation_required" && postedInvoice.sap_reconciliation_blockers?.length > 0 && (
                        <div className="mt-2 border border-amber-300 bg-amber-50 p-2" data-testid="sap-draft-reconciliation-blockers">
                          <p className="text-[11px] font-semibold text-amber-900">Why this is blocked:</p>
                          <ul className="mt-1 list-disc pl-4 text-[11px] text-amber-900">
                            {postedInvoice.sap_reconciliation_blockers.map((reason, i) => (<li key={i}>{reason}</li>))}
                          </ul>
                          <p className="mt-2 text-[11px] text-amber-900">Resolve the blockers in SAP (void the conflicting draft or correct Plant_KUT), then return to Invoice History and use <strong>Verify SAP void and restart</strong>.</p>
                        </div>
                      )}
                      {postedInvoice.sap_calculated_tax_amount !== undefined && postedInvoice.sap_calculated_tax_amount !== null && (
                        <p className="mt-2 text-[11px] text-teal-800" data-testid="sap-calculated-tax">
                          SAP reconciled tax: {fmtCurrency(Number(postedInvoice.sap_calculated_tax_amount))}
                        </p>
                      )}
                      {postedInvoice.sap_grn_links?.length > 0 && (
                        <p className="mt-2 text-[11px] text-teal-800" data-testid="sap-draft-grn-links">
                          Linked GRN: {postedInvoice.sap_grn_links.join(", ")}
                        </p>
                      )}
                      {postedInvoice.sap_responsible_employee_id && (
                        <p className="mt-1 text-[11px] text-teal-800" data-testid="sap-draft-responsible-employee">
                          Responsible employee: {postedInvoice.sap_responsible_employee_id}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </section>
            </div>
          </StepShell>
        </div>
        )}
      </div>
      {/* Supplier Credit slide-over — mounted at root so it survives step changes */}
      <SupplierCreditForm
        open={showSupplierCredit}
        onOpenChange={setShowSupplierCredit}
        parentInvoice={postedInvoice}
      />
      <Dialog open={invoiceCopyModalOpen} onOpenChange={setInvoiceCopyModalOpen}>
        <DialogContent className="max-w-3xl h-[85vh] flex flex-col" data-testid="invoice-copy-modal">
          <DialogHeader>
            <DialogTitle className="pr-8 truncate">Supplier invoice copy — {header.invoice_number || shipmentData?.shipment_code || ""}</DialogTitle>
            <DialogDescription>Scanned supplier invoice fetched from Materials Hub, shown here for comparison while entering invoice details.</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1">
            {invoiceImageType === "application/pdf"
              ? <PdfAttachment src={invoiceImageUrl} />
              : <InvoiceImageZoom src={invoiceImageUrl} alt={`Supplier invoice ${header.invoice_number || shipmentData?.shipment_code || ""}`} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StepShell({ title, description, headerAction, children }) {
  return (
    <div className="p-4 sm:p-6">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-heading font-semibold text-slate-900">{title}</h2>
          <p className="text-[13px] text-slate-500 mt-1">{description}</p>
        </div>
        {headerAction}
      </div>
      {children}
    </div>
  );
}
